import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../context/AppContext";
import AdminNavbar from "../components/AdminNavbar";
import toast from "react-hot-toast";

const FIELDS = [
  {
    key: "address",
    label: "Address",
    icon: "📍",
    placeholder: "12, Green Valley Road, Indiranagar, Bengaluru 560038",
    hint: "Full address shown on the contact page",
    multiline: true,
  },
  {
    key: "phone",
    label: "Phone Number",
    icon: "📞",
    placeholder: "+91 98765 43210",
    hint: "Shown as a clickable call link",
  },
  {
    key: "email",
    label: "Email Address",
    icon: "✉️",
    placeholder: "info@proteinspot.in",
    hint: "Shown as a clickable mailto link",
    type: "email",
  },
  {
    key: "hours",
    label: "Delivery Hours",
    icon: "⏰",
    placeholder: "Every day, 8 am – 9 pm",
    hint: "Shown under the Delivery Hours card",
  },
  {
    key: "mapLabel",
    label: "Map Label",
    icon: "🗺️",
    placeholder: "Indiranagar, Bengaluru",
    hint: "Text shown below the map",
  },
  {
    key: "mapEmbed",
    label: "Map Embed URL",
    icon: "🗺️",
    placeholder: "https://www.google.com/maps/embed?pb=...",
    hint: 'Google Maps → Share → Embed a map → copy only the src="..." value',
  },
];

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 14px",
  borderRadius: 12,
  border: "1.5px solid rgba(45,139,78,0.2)",
  background: "rgba(255,255,255,0.8)",
  fontSize: 13.5,
  color: "#111827",
  outline: "none",
  fontFamily: "inherit",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

export default function AdminContactInfo() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    address: "",
    phone: "",
    email: "",
    hours: "",
    mapLabel: "",
    mapEmbed: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  /* fetch current info */
  useEffect(() => {
    api
      .get("/contact/info")
      .then((res) => {
        setForm(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const set = (key) => (e) => {
    setForm((p) => ({ ...p, [key]: e.target.value }));
    setDirty(true);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/contact/info", form);
      toast.success("Contact info updated!");
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  /* live preview cards */
  const preview = [
    {
      icon: "📍",
      title: "Our Kitchen",
      line1: form.address?.split(",")[0]?.trim(),
      line2: form.address?.split(",").slice(1).join(",").trim(),
    },
    {
      icon: "📞",
      title: "Call Us",
      line1: form.phone,
      line2: "Mon–Sat, 8 am – 8 pm",
    },
    {
      icon: "✉️",
      title: "Email Us",
      line1: form.email,
      line2: "We reply within 24 hours",
    },
    {
      icon: "⏰",
      title: "Delivery Hours",
      line1: form.hours,
      line2: "Fresh prep, zero preservatives",
    },
  ];

  return (
    <div
      className="min-h-screen"
      style={{
        background: "linear-gradient(135deg,#f0f9f0,#e8f5e9)",
        fontFamily: "'Roboto',sans-serif",
      }}
    >
      <AdminNavbar />
      <div className="max-w-screen-lg mx-auto p-4 md:p-8">
        {/* header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin")}
              className="border border-green-500/30 text-green-600 rounded-full px-4 py-1.5 text-sm font-semibold hover:bg-green-500/10 bg-white/60"
            >
              ← Dashboard
            </button>
            <div>
              <h1 className="text-3xl font-black text-green-700">
                Contact Info
              </h1>
              <p className="text-gray-400 text-sm">
                Update the info shown on the public Contact Us page
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-sm font-semibold text-green-600 bg-green-50 border border-green-200 px-3 py-1.5 rounded-full">
                ✓ Saved
              </span>
            )}
            <a
              href="/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-bold px-3 py-1.5 rounded-full border border-green-200 text-green-600 bg-white hover:bg-green-50 transition-all"
            >
              Preview Page ↗
            </a>
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50 transition-all"
              style={{
                background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                boxShadow: dirty ? "0 4px 14px rgba(45,139,78,0.3)" : "none",
              }}
            >
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                  Saving…
                </>
              ) : (
                "💾 Save Changes"
              )}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" />
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 24,
              alignItems: "start",
            }}
          >
            {/* ── Left: edit form ── */}
            <div className="glass-card p-6 flex flex-col gap-5">
              <div>
                <h2 className="font-extrabold text-green-800 text-base mb-1">
                  Edit Details
                </h2>
                <p className="text-xs text-gray-400">
                  Changes are saved to the database and reflected on the Contact
                  page immediately.
                </p>
              </div>

              {FIELDS.map((field) => (
                <div key={field.key}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#374151",
                      marginBottom: 5,
                      textTransform: "uppercase",
                      letterSpacing: ".4px",
                    }}
                  >
                    {field.icon} {field.label}
                  </label>
                  {field.multiline ? (
                    <textarea
                      value={form[field.key] || ""}
                      onChange={set(field.key)}
                      placeholder={field.placeholder}
                      rows={2}
                      style={{
                        ...inputStyle,
                        resize: "vertical",
                        minHeight: 60,
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#2D8B4E";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(45,139,78,0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "rgba(45,139,78,0.2)";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  ) : (
                    <input
                      type={field.type || "text"}
                      value={form[field.key] || ""}
                      onChange={set(field.key)}
                      placeholder={field.placeholder}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#2D8B4E";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(45,139,78,0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "rgba(45,139,78,0.2)";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  )}
                  {field.hint && (
                    <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                      {field.hint}
                    </p>
                  )}
                </div>
              ))}

              <button
                onClick={handleSave}
                disabled={saving || !dirty}
                style={{
                  marginTop: 4,
                  padding: "12px",
                  borderRadius: 12,
                  border: "none",
                  cursor: dirty ? "pointer" : "not-allowed",
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#fff",
                  background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                  opacity: dirty ? 1 : 0.5,
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                {saving ? (
                  <>
                    <span
                      style={{
                        width: 15,
                        height: 15,
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "spin 0.65s linear infinite",
                        display: "inline-block",
                      }}
                    />{" "}
                    Saving…
                  </>
                ) : (
                  "💾 Save Changes"
                )}
              </button>
            </div>

            {/* ── Right: live preview ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <h2 className="font-extrabold text-green-800 text-base mb-1">
                  Live Preview
                </h2>
                <p className="text-xs text-gray-400">
                  Exactly how the cards will appear on the Contact page.
                </p>
              </div>

              {preview.map((item) => (
                <div
                  key={item.title}
                  style={{
                    background: "#fff",
                    borderRadius: 16,
                    padding: "14px 18px",
                    border: "1px solid rgba(45,139,78,0.1)",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                    display: "flex",
                    gap: 14,
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: "rgba(45,139,78,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 17,
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#2D8B4E",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        margin: "0 0 3px",
                      }}
                    >
                      {item.title}
                    </p>
                    <p
                      style={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: item.line1 ? "#111827" : "#d1d5db",
                        margin: 0,
                        wordBreak: "break-word",
                      }}
                    >
                      {item.line1 || "Not set"}
                    </p>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#9ca3af",
                        margin: "2px 0 0",
                      }}
                    >
                      {item.line2}
                    </p>
                  </div>
                </div>
              ))}

              {/* map preview — uses mapEmbed src directly */}
              <div
                style={{
                  borderRadius: 16,
                  overflow: "hidden",
                  border: "1px solid rgba(45,139,78,0.12)",
                  height: 160,
                  position: "relative",
                }}
              >
                {form.mapEmbed ? (
                  <>
                    <iframe
                      title="Map Preview"
                      width="100%"
                      height="100%"
                      style={{ border: 0, display: "block" }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={form.mapEmbed}
                    />
                  </>
                ) : (
                  <div
                    style={{
                      height: "100%",
                      background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                    }}
                  >
                    <div style={{ fontSize: 28 }}>🗺️</div>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#374151",
                        fontWeight: 600,
                        margin: 0,
                      }}
                    >
                      Paste the embed URL above to preview
                    </p>
                    <p
                      style={{
                        fontSize: 11,
                        color: "#9ca3af",
                        margin: "2px 0 0",
                        textAlign: "center",
                        maxWidth: 200,
                      }}
                    >
                      Maps → Share → Embed a map → copy src value
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media(max-width:768px) {
          .max-w-screen-lg > div:last-child > div:last-child { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
